import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import commonEssence from '@sorare/core/src/assets/rewards/common-essence.png';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Snackbar } from '@sorare/core/src/atoms/snackbar/Snackbar';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { SelectionSummary } from '@sorare/core/src/components/layout/SelectionSummary';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { commonMarketplace } from '@sorare/core/src/lib/glossary';
import { Color } from '@sorare/core/src/style/types';

import { useDropAndAddCards } from '@sorare/marketplace/src/components/commonCard/CommonPaymentBox/useDropAndAddCards';

import {
  CardsDropSummaryQuery,
  CardsDropSummaryQueryVariables,
} from './__generated__/index.graphql';

type Props = {
  rarity: Rarity;
  onCancel: () => void;
  onDropped: (droppedCardSlugs: string[]) => void;
  selectedCardsSlugs: Nullable<string[]>;
};

const CARDS_DROP_SUMMARY_QUERY = gql`
  query CardsDropSummaryQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      signedDropCardPrice {
        price
        signedPrice
      }
    }
  }
` as TypedDocumentNode<CardsDropSummaryQuery, CardsDropSummaryQueryVariables>;

export const CardsDropSummary = ({
  rarity,
  onCancel,
  onDropped,
  selectedCardsSlugs,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { data, loading } = useQuery(CARDS_DROP_SUMMARY_QUERY, {
    variables: {
      slugs: selectedCardsSlugs || [],
    },
    skip: !selectedCardsSlugs?.length,
  });
  const selectedCards =
    data?.anyCards?.filter(card => selectedCardsSlugs?.includes(card.slug)) ||
    [];
  const totalPoints = selectedCards.reduce(
    (acc, card) => acc + (card.signedDropCardPrice?.price || 0),
    0
  );
  const [displayedTotalPoints, setDisplayedTotalPoints] = useState(totalPoints);

  if (displayedTotalPoints !== totalPoints && !loading) {
    setDisplayedTotalPoints(totalPoints);
  }

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const { dropAndAddCards, loading: mutationLoading } = useDropAndAddCards();

  return (
    <>
      <SelectionSummary
        nbCardsSelected={selectedCardsSlugs?.length || 0}
        color={'rgb(var(--c-rgb-common))' as Color}
        onCancel={onCancel}
        button={
          <LoadingButton
            loading={mutationLoading}
            startIcon={<DropCards />}
            size="medium"
            color="secondary"
            onClick={async () => {
              const result = await dropAndAddCards(
                selectedCards
                  .filter(card => !!card.signedDropCardPrice)
                  .map(card => ({
                    cardSlug: card.slug,
                    signedPrice: card.signedDropCardPrice!.signedPrice,
                  })),
                []
              );
              const droppedCards = result.data?.dropAndAddCards?.droppedCards;
              if (droppedCards?.length) {
                setSnackbarMessage(`You gained ${totalPoints} Common Essence`);
                onDropped?.(droppedCards.map(card => card.slug));
              }
            }}
            disabled={!selectedCardsSlugs?.length}
          >
            <FormattedMessage {...commonMarketplace.drop} />
          </LoadingButton>
        }
      >
        <Horizontal>
          <Horizontal gap={0.5}>
            <NewScarcityIcon rarity={rarity} size={2} />
            <LabelL bold>
              <FormattedNumber value={(selectedCardsSlugs || []).length} />
            </LabelL>
          </Horizontal>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="4"
            height="4"
            viewBox="0 0 4 4"
            fill="none"
          >
            <circle cx="2" cy="2" r="2" fill="white" fillOpacity="0.17" />
          </svg>
          <Horizontal gap={0.5}>
            <img src={commonEssence} alt="" height={16} />
            <LabelL bold>
              <FormattedNumber value={currentUser?.commonCardPoints || 0} />
            </LabelL>
            {!!displayedTotalPoints && (
              <LabelL color="var(--c-rivals-win)">
                (+
                <AnimatedNumber value={displayedTotalPoints} />)
              </LabelL>
            )}
          </Horizontal>
        </Horizontal>
      </SelectionSummary>
      <Snackbar
        open={!!snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        autoHideDuration={10000}
      />
    </>
  );
};
